import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-display'
import type { FolderImportPreview } from '@st/sdk'
import { Button, FolderArrowDownIcon, Spinner, Wizard } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { clientPacketsFlowDefinition, type ClientPacketsFlowId } from './client-packets-flow'
import { selClientPacketJobIds, type ClientPacketsState } from './client-packets-module'

type Props = {
  onClose: () => void
  flow: ClientPacketsFlowId
}

type PacketRow = {
  key: string
  client: string
  type: string
  year: number
}

export function ClientPacketsPage({ onClose, flow }: Props) {
  const clientPackets = useProcess('clientPackets')
  const state = useSelect('clientPackets', (s) => s)
  const flowDefinition = clientPacketsFlowDefinition(flow)

  if (state.step == 'idle') {
    return null
  }

  return (
    <Wizard.Scaffold
      breadcrumb={`Generate ${flowDefinition.plural}`}
      onClose={onClose}
      bottomNav={bottomNav(state, flowDefinition, () =>
        clientPackets.send({ type: 'exportPackets' })
      )}
    >
      {mainContent(state, flowDefinition)}
    </Wizard.Scaffold>
  )
}

function bottomNav(
  state: ClientPacketsState,
  flow: ReturnType<typeof clientPacketsFlowDefinition>,
  exportPackets: () => void
) {
  if (state.step == 'ready') {
    const jobCount = selClientPacketJobIds(state.importState).length
    return (
      <Wizard.BottomNav>
        <div></div>
        <Button variant="primary" disabled={jobCount == 0} onClick={exportPackets}>
          Generate {inflect(jobCount, flow.singular)}
        </Button>
      </Wizard.BottomNav>
    )
  }

  return null
}

function mainContent(
  state: ClientPacketsState,
  flow: ReturnType<typeof clientPacketsFlowDefinition>
) {
  switch (state.step) {
    case 'loading':
      return (
        <Processing
          title="Processing backup..."
          subtitle="This may take 2-3 minutes to complete."
        />
      )
    case 'exporting':
      return (
        <Processing
          title={`StanfordTax is generating ${flow.descriptivePlural} for your clients using their prior year information.`}
        />
      )
    case 'ready':
      return <ReadyContent importState={state.importState} flow={flow} />
    case 'exported':
      return <ExportedContent state={state} flow={flow} />
    case 'idle':
      return null
  }
}

function ReadyContent({
  importState,
  flow
}: {
  importState: FolderImportPreview
  flow: ReturnType<typeof clientPacketsFlowDefinition>
}) {
  if (importState.error) {
    return <Message title={importState.error.message} preserveWhitespace={true} />
  }

  if (importState.folders.length == 0) {
    return <Message title="No clients were found in the upload." />
  }

  const jobCount = selClientPacketJobIds(importState).length

  return (
    <Wizard.Main
      variant="wide"
      title={`Generate ${flow.plural}`}
      subtitle={`Below are the clients that were in the backup file. StanfordTax will generate ${jobCount} ${inflect(
        jobCount,
        flow.singular
      )}.`}
    >
      <ClientPacketsTable importState={importState} />
    </Wizard.Main>
  )
}

function ExportedContent({
  state,
  flow
}: {
  state: Extract<ClientPacketsState, { step: 'exported' }>
  flow: ReturnType<typeof clientPacketsFlowDefinition>
}) {
  return (
    <Wizard.Main variant="wide">
      <div className="flex h-full w-full flex-col items-center justify-center gap-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
          <FolderArrowDownIcon className="size-8" />
        </div>
        <div className="flex max-w-[520px] flex-col items-center gap-2">
          <div className="text-lg font-semibold text-gray-900">{flow.titlePlural} generated</div>
          <div className="text-sm text-gray-600">
            Your {flow.descriptivePlural} are ready to download.
          </div>
        </div>
        <Button
          variant="primary"
          href={state.exportResult.downloadUrl}
          target="_blank"
          leadingIcon={<FolderArrowDownIcon className="size-5" />}
        >
          Download {flow.plural}
        </Button>
      </div>
    </Wizard.Main>
  )
}

function Message({
  title,
  preserveWhitespace = false
}: {
  title: string
  preserveWhitespace?: boolean
}) {
  return (
    <Wizard.Main variant="wide">
      <div
        className={`flex h-full flex-col items-center justify-center gap-4 text-center text-lg font-semibold text-gray-900 ${
          preserveWhitespace ? 'whitespace-pre' : ''
        }`}
      >
        {title}
      </div>
    </Wizard.Main>
  )
}

function Processing({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Wizard.Main variant="wide">
      <div className="flex h-full w-full flex-col items-center justify-center gap-6">
        <Spinner className="size-16 fill-blue-600 text-gray-200" />
        <div className="flex max-w-[620px] flex-col items-center gap-2">
          <div className="text-center text-lg font-semibold text-gray-700">{title}</div>
          {subtitle ? <div className="text-center text-sm text-gray-700">{subtitle}</div> : null}
        </div>
      </div>
    </Wizard.Main>
  )
}

function ClientPacketsTable({ importState }: { importState: FolderImportPreview }) {
  return (
    <table className="min-w-full table-fixed divide-y divide-gray-300">
      <thead className="sticky top-0 z-10 bg-white">
        <tr className="h-9">
          {['Client', 'Type', 'Year'].map((heading, index) => (
            <th
              key={heading}
              scope="col"
              className={`text-left text-xs uppercase text-gray-600 ${index == 0 ? 'pl-8' : ''} ${
                index == 2 ? 'pr-8' : ''
              }`}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {packetRows(importState).map((row) => (
          <tr key={row.key} className="h-12">
            <td className="whitespace-nowrap pl-8">
              <div className="text-base font-semibold">{row.client}</div>
            </td>
            <td className="whitespace-nowrap text-base text-gray-500">{row.type}</td>
            <td className="whitespace-nowrap pr-8 text-base text-gray-500">{row.year}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function packetRows(importState: FolderImportPreview): PacketRow[] {
  const includedJobIds = new Set(selClientPacketJobIds(importState))

  return importState.folders
    .filter((job) => includedJobIds.has(job.taxReturnId))
    .map(
      (job): PacketRow => ({
        key: job.taxReturnId,
        client: formatFolderEntityName(job.entities),
        type: job.returnType,
        year: job.year
      })
    )
}
